import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp,
  Users, Car, Bell, Settings, MapPin, Calendar, Clock, Package, Eye,
  Filter, Search, MoreVertical, Edit, Trash2, UserCheck, UserX,
  AlertTriangle, CheckCircle, XCircle, Phone, CreditCard
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useTransporterDrivers } from '@/hooks/useTransporterDrivers';
import { useTransporterVehicles } from '@/hooks/useTransporterVehicles';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';
import useAuthStore from '@/store/authStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';

const TransporterDrivers = () => {
  const { stats } = useTransporterStats();
  const { vehicles } = useTransporterVehicles();
  const { user } = useAuthStore();
  const [filters, setFilters] = useState({
    search: '',
    assigned: 'all',
  });
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverToAssign, setDriverToAssign] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [driverData, setDriverData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    license_number: '',
    license_valid_until: '',
  });

  const { drivers, loading, error, createDriver, updateDriver, deleteDriver, assignVehicle, unassignVehicle } = useTransporterDrivers(filters);

  const sidebarLinks = getTransporterSidebarLinks(stats);

  const getAssignmentBadge = (assigned) => {
    if (assigned) {
      return <Badge className="bg-green-500 text-white">Assigné</Badge>;
    }
    return <Badge className="bg-gray-500 text-white">Non assigné</Badge>;
  };

  const getLicenseStatusBadge = (licenseValidUntil) => {
    if (!licenseValidUntil) return <Badge className="bg-gray-500 text-white">Non spécifié</Badge>;

    const date = new Date(licenseValidUntil);
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    if (date < now) {
      return <Badge className="bg-red-500 text-white">Expiré</Badge>;
    } else if (date <= thirtyDaysFromNow) {
      return <Badge className="bg-orange-500 text-white">Expire bientôt</Badge>;
    } else {
      return <Badge className="bg-green-500 text-white">Valide</Badge>;
    }
  };

  const handleCreateDriver = () => {
    setEditingDriver(null);
    setDriverData({
      first_name: '',
      last_name: '',
      phone: '',
      license_number: '',
      license_valid_until: '',
    });
    setDriverDialogOpen(true);
  };

  const handleEditDriver = (driver) => {
    setEditingDriver(driver);
    setDriverData({
      first_name: driver.first_name,
      last_name: driver.last_name,
      phone: driver.phone,
      license_number: driver.license_number,
      license_valid_until: driver.license_valid_until || '',
    });
    setDriverDialogOpen(true);
  };

  const handleSubmitDriver = async () => {
    if (!driverData.first_name || !driverData.last_name || !driverData.phone || !driverData.license_number) {
      toast({
        title: 'Erreur',
        description: 'Tous les champs obligatoires doivent être remplis.',
        variant: 'destructive',
      });
      return;
    }

    // Récupérer l'ID du transporteur depuis le profil utilisateur
    const transporterId = user?.profile_data?.id;
    if (!transporterId && !editingDriver) {
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer l\'ID du transporteur. Veuillez vous reconnecter.',
        variant: 'destructive',
      });
      return;
    }

    // Préparer les données à envoyer avec le champ transporter si c'est une création
    const submitData = { ...driverData };
    if (!editingDriver && transporterId) {
      submitData.transporter = transporterId;
    }

    const result = editingDriver
      ? await updateDriver(editingDriver.id, submitData)
      : await createDriver(submitData);

    if (result.success) {
      toast({
        title: editingDriver ? 'Chauffeur modifié' : 'Chauffeur créé',
        description: `Le chauffeur a été ${editingDriver ? 'modifié' : 'créé'} avec succès.`,
      });
      setDriverDialogOpen(false);
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteDriver = (driver) => {
    setDriverToDelete(driver);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDriver = async () => {
    const result = await deleteDriver(driverToDelete.id);
    if (result.success) {
      toast({
        title: 'Chauffeur supprimé',
        description: 'Le chauffeur a été supprimé avec succès.',
      });
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
    setDeleteDialogOpen(false);
    setDriverToDelete(null);
  };

  const handleAssignVehicle = (driver) => {
    setDriverToAssign(driver);
    setAssignDialogOpen(true);
  };

  const handleUnassignVehicle = async (driver) => {
    const result = await unassignVehicle(driver.id);
    if (result.success) {
      toast({
        title: 'Véhicule désassigné',
        description: 'Le véhicule a été désassigné avec succès.',
      });
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitAssignment = async (vehicleId) => {
    const result = await assignVehicle(driverToAssign.id, vehicleId);
    if (result.success) {
      toast({
        title: 'Véhicule assigné',
        description: 'Le véhicule a été assigné avec succès.',
      });
      setAssignDialogOpen(false);
      setDriverToAssign(null);
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <>
      <Helmet>
        <title>Gestion des Chauffeurs - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Gestion des Chauffeurs</h1>
            <p className="text-muted-foreground mt-2">Gérez votre équipe de chauffeurs</p>
          </div>
          <Button onClick={handleCreateDriver} className="bg-primary hover:bg-primary/90">
            <Plus className="w-5 h-5 mr-2" />
            Ajouter un chauffeur
          </Button>
        </div>

        {/* Filtres */}
        <Card className="mb-6 bg-card/50 border-border">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher un chauffeur..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filters.assigned} onValueChange={(value) => setFilters(prev => ({ ...prev, assigned: value }))}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filtrer par assignation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="true">Assignés</SelectItem>
                  <SelectItem value="false">Non assignés</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Liste des chauffeurs */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Chargement des chauffeurs...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-destructive">Erreur: {error}</div>
          </div>
        ) : drivers.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Aucun chauffeur trouvé</h3>
              <p className="text-muted-foreground">Commencez par ajouter votre premier chauffeur.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {drivers.map((driver) => (
              <Card
                key={driver.id}
                className="group border-border bg-card/50 hover:border-secondary hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col gap-4">
                    {/* En-tête avec photo et nom */}
                    <div className="flex items-start gap-4">
                      <Avatar className="w-16 h-16 flex-shrink-0 border-2 border-border">
                        <AvatarImage
                          src={driver.profile_image || driver.photo || `https://api.dicebear.com/7.x/initials/svg?seed=${driver.first_name}+${driver.last_name}`}
                          alt={`${driver.first_name} ${driver.last_name}`}
                        />
                        <AvatarFallback className="bg-primary/20 text-primary text-lg font-bold">
                          {driver.first_name?.[0] || 'D'}{driver.last_name?.[0] || 'D'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-bold text-foreground">
                            {driver.first_name} {driver.last_name}
                          </h3>
                          {getAssignmentBadge(!!driver.assigned_vehicle)}
                          {getLicenseStatusBadge(driver.license_valid_until)}
                        </div>
                        {driver.logiconnect_id && (
                          <p className="text-xs text-muted-foreground">ID: {driver.logiconnect_id}</p>
                        )}
                      </div>
                    </div>

                    {/* Informations du chauffeur */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground">{driver.phone}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <CreditCard className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground">
                          Permis: <span className="font-semibold text-foreground">{driver.license_number}</span>
                        </span>
                      </div>

                      {driver.license_valid_until && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground">
                            Valide jusqu'au: <span className="font-semibold text-foreground">{formatDate(driver.license_valid_until)}</span>
                          </span>
                        </div>
                      )}

                      {driver.assigned_vehicle && (
                        <div className="flex items-center gap-2 text-sm p-3 bg-primary/5 rounded-lg border border-border/50">
                          <Car className="w-4 h-4 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="text-muted-foreground">Véhicule assigné: </span>
                            <span className="font-semibold text-foreground">{driver.assigned_vehicle.plate_number}</span>
                            {driver.assigned_vehicle_make_model && (
                              <span className="text-muted-foreground ml-2">
                                ({driver.assigned_vehicle_make_model})
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="pt-3 border-t border-border flex gap-2">
                      {driver.assigned_vehicle ? (
                        <Button
                          onClick={() => handleUnassignVehicle(driver)}
                          size="sm"
                          variant="outline"
                          className="flex-1 text-orange-600 border-orange-600 hover:bg-orange-50"
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Désassigner
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleAssignVehicle(driver)}
                          size="sm"
                          variant="outline"
                          className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Assigner
                        </Button>
                      )}
                      <Button
                        onClick={() => handleEditDriver(driver)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      <Button
                        onClick={() => handleDeleteDriver(driver)}
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog pour créer/modifier un chauffeur */}
        <Dialog open={driverDialogOpen} onOpenChange={setDriverDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingDriver ? 'Modifier le chauffeur' : 'Ajouter un chauffeur'}
              </DialogTitle>
              <DialogDescription>
                {editingDriver ? 'Modifiez les informations du chauffeur.' : 'Ajoutez un nouveau chauffeur à votre équipe.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    value={driverData.first_name}
                    onChange={(e) => setDriverData(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder="Ex: Mamadou"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    value={driverData.last_name}
                    onChange={(e) => setDriverData(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder="Ex: Diallo"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  value={driverData.phone}
                  onChange={(e) => setDriverData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Ex: +224 123 45 67 89"
                />
              </div>

              <div>
                <Label htmlFor="license_number">Numéro de permis *</Label>
                <Input
                  id="license_number"
                  value={driverData.license_number}
                  onChange={(e) => setDriverData(prev => ({ ...prev, license_number: e.target.value }))}
                  placeholder="Ex: AB123456789"
                />
              </div>

              <div>
                <Label htmlFor="license_valid_until">Permis valide jusqu'au</Label>
                <Input
                  id="license_valid_until"
                  type="date"
                  value={driverData.license_valid_until}
                  onChange={(e) => setDriverData(prev => ({ ...prev, license_valid_until: e.target.value }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDriverDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSubmitDriver}>
                {editingDriver ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog pour assigner un véhicule */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assigner un véhicule</DialogTitle>
              <DialogDescription>
                Sélectionnez un véhicule à assigner à {driverToAssign?.first_name} {driverToAssign?.last_name}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="vehicle">Véhicule</Label>
                <Select onValueChange={handleSubmitAssignment}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un véhicule" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles
                      .filter(vehicle => vehicle.status === 'AVAILABLE')
                      .map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.plate_number} - {vehicle.vehicle_type?.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Annuler
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <ConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Supprimer le chauffeur"
          description={`Êtes-vous sûr de vouloir supprimer ${driverToDelete?.first_name} ${driverToDelete?.last_name} ? Cette action est irréversible.`}
          onConfirm={confirmDeleteDriver}
        />
      </DashboardLayout>
    </>
  );
};

export default TransporterDrivers;
